import {ChangeEvent, DragEvent, Fragment, useRef, useState} from "react";
import {BulkDeleteClientError} from "../../BulkDeleteUrlsState";
import {formatRowLimit, formatUploadLimit} from "../../bulkDeleteLimits";

type BulkDeleteUploadProps = {
  readonly file: File | null;
  readonly previewErrorMessage: string | null;
  readonly clientError: BulkDeleteClientError | null;
  readonly onFileSelected: (file: File | null) => void;
  readonly onRemoveFile: () => void;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const BulkDeleteUpload = (props: BulkDeleteUploadProps): JSX.Element => {
  const {file, previewErrorMessage, clientError, onFileSelected, onRemoveFile} = props;
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const openFilePicker = () => fileInputRef.current?.click();

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    onFileSelected(event.target.files?.[0] ?? null);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    onFileSelected(event.dataTransfer.files?.[0] ?? null);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const onDragLeave = () => setIsDragOver(false);

  const handleRemove = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    onRemoveFile();
  };

  return (
    <Fragment>
      <p className="text-muted">
        Upload a CSV of cached_urls rows to delete cached pages for this user in bulk. A dry-run preview shows exactly what will be removed
        before anything is deleted.
      </p>
      <label htmlFor="bulk-delete-file" className="bulk-delete-visually-hidden">
        CSV file
      </label>
      <input
        ref={fileInputRef}
        type="file"
        id="bulk-delete-file"
        className="bulk-delete-visually-hidden"
        accept=".csv,text/csv"
        onChange={onFileChange}
      />
      {!file && (
        <div
          className={`bulk-delete-dropzone ${isDragOver ? "is-dragover" : ""}`}
          role="button"
          tabIndex={0}
          onClick={openFilePicker}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") openFilePicker();
          }}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
        >
          <div className="bulk-delete-dropzone-icon" aria-hidden="true">
            ⬆
          </div>
          <div className="bulk-delete-dropzone-title">Drop CSV here or click to browse</div>
          <div className="bulk-delete-dropzone-hint">
            Max {formatUploadLimit()} · up to {formatRowLimit()} rows
          </div>
          <div className="bulk-delete-required-columns">required columns: id · url · adaptive_type · user_id</div>
        </div>
      )}
      {file && (
        <div className="bulk-delete-file-pill">
          <span aria-hidden="true">📄</span>
          <span className="bulk-delete-file-name">{file.name}</span>
          <span className="bulk-delete-file-meta">{formatFileSize(file.size)}</span>
          <button type="button" className="bulk-delete-file-remove" aria-label="Remove file" onClick={handleRemove}>
            ✕
          </button>
        </div>
      )}
      {clientError && <div className="alert alert-danger mt-3">{clientError.message}</div>}
      {previewErrorMessage && <div className="alert alert-danger mt-3">{previewErrorMessage}</div>}
    </Fragment>
  );
};

export default BulkDeleteUpload;
